import { CommonModule, TitleCasePipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import { Pagination } from '@core/models/pagination.model';
import { AccountManagementService } from '@core/services/account-management.service';
import { LoadingService } from '@core/services/loading.service';
import { ConsolidatedListRefreshService } from '@core/services/consolidated-list-refresh.service';
import { MutualFundService } from '@core/services/mutual-fund.service';
import {
  DropdownComponent,
  DropDownModel,
} from '@shared/ui/dropdown/dropdown.component';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { IFILTER } from 'app/app.constants';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { PermissionDirective } from '@shared/directives/permission.directive';
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';
@Component({
  selector: 'app-consolidated-mutual-funds',
  standalone: true,
  imports: [
    DropdownComponent,
    CommonModule,
    TableSkeletonLoaderComponent,
     FiltersComponent,
    RouterOutlet,
    TableNavigatorComponent,
    PermissionDirective,
  ],
  templateUrl: './consolidated-mutual-funds.component.html',
  styleUrl: './consolidated-mutual-funds.component.scss',
  providers: [TitleCasePipe, UpperCasePipe]
})
export class ConsolidatedMutualFundsComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private mutualFundService = inject(MutualFundService);
  private change = inject(ChangeDetectorRef);
  private loadingService = inject(LoadingService);
  private notify = inject(NotificationFacade);
  private accountService = inject(AccountManagementService);
  private permissionService = inject(RolesAndPermissionsService);
  private listRefresh = inject(ConsolidatedListRefreshService);

  totalCount: number = 0;
  subs: Subscription[] = [];
  isFetchingMutualFundsList$!: Observable<boolean>;
  actionList: DropDownModel[] = [];
  start_date: Date | undefined;
  end_date: Date | undefined;
  searchText: string = '';
  currentPagination: Pagination = new Pagination();

  private mutualFundsListSubject = new BehaviorSubject<any[]>([]);
  mutualFundsList$ = this.mutualFundsListSubject.asObservable();

  filterStructure: IFILTER[] = [
    {
      filterKey: 'date',
      label: 'Date',
      type: 'date',
    },
  ];

  constructor(private titleCasePipe: TitleCasePipe, private upperCasePipe: UpperCasePipe) {
    this.isFetchingMutualFundsList$ = this.loadingService.getLoadingObservable(
      'get-mutual-funds-list'
    );
  }

  ngOnInit() {
    // listener to the service to reload
    this.subs.push(
      this.listRefresh.refreshRequested$.subscribe(() =>
        this.reloadListUnfiltered()
      )
    );

    const sub = this.activatedRoute.queryParams.subscribe((params) => {
      this.searchText = params['search'] || '';
      this.start_date = params['start_date']
        ? new Date(params['start_date'])
        : undefined;
      this.end_date = params['end_date']
        ? new Date(params['end_date'])
        : undefined;

      if (this.start_date && this.end_date) {
        this.getMutualFundsList(
          new Pagination(),
          this.searchText,
          this.start_date,
          this.end_date
        );
      } else {
        this.getMutualFundsList( new Pagination(), this.searchText);
      }
    });
    this.subs.push(sub);
  }

  initDropdownList(param: any) {
    return (this.actionList = [
      {
        title: '+ MF Transaction',
        action: () => {
          this.navigateToAddMFTransaction(param.account_number);
        },
        show: this.permissionService.hasPermission(['can_add_mutual_fund_subscription'])
      },

      {
        action: () => {
          this.transactionHistory(param.account_number);
        },
        title: 'Transaction History',
        show: true && this.permissionService.hasPermission(['can_get_all_funds_transaction']),
      },

      {
        action: () => {
          this.dividentHistory(param.account_number);
        },
        title: 'Dividends History',
        show: true && this.permissionService.hasPermission(['can_export_individual_dividend_report']),
      },
      {
        action: () => {
          this.exportReport(param.account_number);
        },
        title: 'Export Report',
        show: true && this.permissionService.hasPermission(['can_generate_mutual_funds_statement']),
      },
    ]);
  }

  transactionHistory(account_number: string) {
    this.router.navigate([
      `admin/transactions/mutual-funds/consolidated/${account_number}/mutual-funds-history`,
    ]);
  }
  dividentHistory(account_number: string) {
    this.router.navigate([
      `admin/transactions/mutual-funds/consolidated/${account_number}/dividends-history`,
    ]);
  }
  navigateToAddMFTransaction(account_id: string) {
    this.router.navigateByUrl(
      `/admin/transactions/mutual-funds/consolidated/add-transaction?account_number=${account_id}`
    );
  }

  exportReport(account_number: string) {
    const sub = this.accountService
      .getAccountDetails(account_number)
      .subscribe({
        next: (res) => {
          this.router.navigate(
            ['admin/transactions/mutual-funds/consolidated/export-mfd-report'],
            { queryParams: { 
              accountId: res?.data[0]?.id ,
              accountNumber: account_number,
            } 
          }
          );
        },
        error: () => {},
      });
    this.subs.push(sub);
  }

  getMutualFundsList(
    page: Pagination,
    search_text: string = '',
    start_date?: Date,
    end_date?: Date,
  ) {
    this.currentPagination = page;
    const sub = this.mutualFundService
      .getMutualFundsList(search_text, start_date, end_date, page)
      .subscribe({
        next: (res) => {
          this.totalCount = res.data.count;
          this.mutualFundsListSubject.next(res.data.data);
          // this.change.detectChanges();
        },
        error: () => {},
      });
    this.subs.push(sub);
  }

  private reloadListUnfiltered() {
    this.searchText = '';
    this.start_date = undefined;
    this.end_date = undefined;

    if (Object.keys(this.activatedRoute.snapshot.queryParams).length) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {},
        replaceUrl: true,
      });
      return;
    }

    this.getMutualFundsList(new Pagination());
  }

  viewAccountDetails(account_number: string) {
    const sub = this.accountService
      .getAccountDetails(account_number)
      .subscribe({
        next: (res) => {
          this.router.navigate([
            `/admin/accounts/${res?.data[0]?.account_type}/${res?.data[0]?.id}/portfolio/${account_number}/portfolio`,
          ]);
        },
        error: () => {},
      });
    this.subs.push(sub);
  }

  navigate(currentPage: number) {
    const skip = currentPage * 10 - 10;
    this.getMutualFundsList(
      new Pagination({ skip }),
      this.searchText,
      this.start_date,
      this.end_date
    );
  }

  formatName(name: string): string {
    if (!name) return '-';
    
    const parts = name.trim().split(' ');
    
    if (parts.length === 0) return '-';
    if (parts.length === 1) return this.titleCasePipe.transform(parts[0]) || '';
    if (parts.length === 2) {
      // Only first and last name
      const firstName = this.titleCasePipe.transform(parts[0]) || '';
      const lastName = this.upperCasePipe.transform(parts[1]) || '';
      return `${firstName} ${lastName}`.trim();
    }
    
    // Three or more parts: first name goes to end in uppercase, rest are title case
    const firstName = this.upperCasePipe.transform(parts[0]) || '';
    const middleAndLastNames = parts.slice(1)
      .map(name => this.titleCasePipe.transform(name))
      .join(' ');
    
    return `${middleAndLastNames} ${firstName}`.trim();
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
